import React from 'react';
import type { FormDefinition } from '@gov-design-system-ce/react-form-core';
import { ApplicantFO, MarkdownRender } from '@gov-nx/component/web';
import type { useApplicantLoader } from '@gov-nx/core/hooks';
import { useTranslationWithNamespace } from '@gov-nx/core/service';
import type { EPeticeFormDataStep1, PageCode } from '@gov-nx/module/page';
import { FormRadio, FormSideOffsetLayout } from '@gov-nx/ui/web';
import { ApplicantPO } from './ApplicantPO';

interface FounderSelectProps {
	code: PageCode;
	isDisabled: boolean;
	formDefinition: FormDefinition<EPeticeFormDataStep1>;
	applicant: ReturnType<typeof useApplicantLoader>;
}

export const FounderSelect = ({
	applicant,
	formDefinition,
	code,
	isDisabled,
}: FounderSelectProps) => {
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const tsn = getLocalizeCurried(code);

	if (applicant.businessPerson?.seznam?.length && applicant.individualPerson) {
		return (
			<FormRadio
				field={{
					name: 'zakladatelTyp',
					isDisabled: isDisabled,
					size: 'm',
					options: [
						{
							value: 'FO',
							label: { children: tsn('formular.pole.zakladatel-typ-fo') },
							children:
								formDefinition.formMethods.getValues('zakladatelTyp') ===
								'FO' ? (
									<FormSideOffsetLayout
										gap="m"
										className="w-full">
										<MarkdownRender className="pt-(--templates-margin-s)!">
											{tsn('formular.popis.zakladni-udaje')}
										</MarkdownRender>
										<ApplicantFO
											strings={{
												nameLabel: tsn('formular.zakladatel.jmeno'),
												surnameLabel: tsn('formular.zakladatel.prijmeni'),
												dateOfBirthLabel: tsn(
													'formular.zakladatel.datum-narozeni'
												),
												addressLabel: tsn('formular.zakladatel.adresa'),
											}}
											person={applicant.individualPerson}
										/>
									</FormSideOffsetLayout>
								) : undefined,
						},
						{
							value: 'PO',
							label: { children: tsn('formular.pole.zakladatel-typ-po') },
							children:
								formDefinition.formMethods.watch('zakladatelTyp') === 'PO' ? (
									<FormSideOffsetLayout
										gap="m"
										className="w-full">
										<ApplicantPO code={code} />
									</FormSideOffsetLayout>
								) : undefined,
						},
					],
				}}
				label={{ children: tsn('formular.pole.zakladatel-typ') }}
			/>
		);
	}

	if (applicant.individualPerson) {
		return (
			<ApplicantFO
				strings={{
					nameLabel: tsn('formular.zakladatel.jmeno'),
					surnameLabel: tsn('formular.zakladatel.prijmeni'),
					dateOfBirthLabel: tsn('formular.zakladatel.datum-narozeni'),
					addressLabel: tsn('formular.zakladatel.adresa'),
				}}
				person={applicant.individualPerson}
			/>
		);
	}
	if (applicant.businessPerson) {
		return <ApplicantPO code={code} />;
	}
	return null;
};
